import Amplifier from '../models/Amplifier';
import commandsProvider from './CommandsProvider';
import isEqual from 'lodash/isEqual';

class AmplifiersProvider {
    constructor() {
        this.amplifiers = new Map(); // Map<serialNumber, Amplifier>
        this.subscribers = [];
        this.processingQueue = false;
        this.initialized = false;
        this.bridgeMode = sessionStorage.getItem("bridgeMode") === "true";
        this.allowNewAmplifiers = false;
        this.syncTimer = 0;
        this.offlineTimer = 0;
        this.changeQueue = new Map();
        this.processingQueue = false;

        // Observa mudanças no CommandsProvider para reagir a alarmes
        commandsProvider.subscribe((state) => {
            // Reage a alarmes
            if (state.currentAlarm) {
                this.handleAlarmState();
            }

            // Inicializa quando o serial estiver disponível
            if (state.session?.serial || sessionStorage.getItem("serial")) {
                const panelSerial = state.session?.serial || sessionStorage.getItem("serial");
                if (!this.initialized && panelSerial) {
                    this.initialize(panelSerial).catch(error => {
                        console.error('Erro ao inicializar AmplifiersProvider:', error);
                    });
                }
            }
        });
    }

    // Inicialização e Carregamento
    async initialize(panelSerial) {
        if (this.initialized || !panelSerial) return;
    
        try {
            console.log('Inicializando AmplifiersProvider com serial:', panelSerial);
            
            await this.loadTimers();
            await this.loadBridgeMode();
            await this.loadAllowNewAmplifiers(panelSerial);
            await this.loadAmplifiers(panelSerial);

            this.initialized = true;
            console.log('AmplifiersProvider inicializado com sucesso');
        } catch (error) {
            console.error('Falha ao inicializar AmplifiersProvider:', error);
            // Permite nova tentativa em caso de falha
            this.initialized = false;
            throw error;
        }
    }

    async loadAmplifiers(panelSerial) {
        console.log('Loading amplifiers for serial:', panelSerial);
        try {
            const response = await fetch(`${process.env.REACT_APP_URL}/amplifier/get-by-serial`, {
                method: "POST",
                headers: {
                    "content-type": "application/json",
                    "Authorization": "Bearer " + localStorage.getItem("token")
                },
                body: JSON.stringify(panelSerial)
            });

            if (!response.ok) throw new Error("Falha ao carregar amplificadores");
            
            const data = await response.json();
            console.log('Received amplifiers data:', data);

            // Validação dos dados recebidos
            if (!data || !Array.isArray(data.amplifiers)) {
                console.error('Dados inválidos recebidos:', data);
                throw new Error("Formato de dados inválido");
            }
            
            // Limpa mapa atual
            this.amplifiers.clear();
            
            // Popula com novos dados
            data.amplifiers.forEach(amp => {
                console.log('Processando amplificador:', amp.dS_DL);
                const amplifier = Amplifier.fromDatabase(amp);
                this.amplifiers.set(amp.dS_DL, amplifier); // Usa dS_DL diretamente como chave
            });

            console.log('Amplifiers map updated:', this.amplifiers);
            this.notifySubscribers();
            return Array.from(this.amplifiers.values());
        } catch (error) {
            console.error("Erro ao carregar amplificadores:", error);
            this.notifySubscribers(); // Notifica mesmo com erro para atualizar UI
            throw error;
        }
    }

    // Gerenciamento de Estado
    getAmplifier(serialNumber) {
        return this.amplifiers.get(serialNumber);
    }

    getCentral() {
        return Array.from(this.amplifiers.values())
            .find(amp => amp.getName() === 'Central');
    }

    getModem() {
        return Array.from(this.amplifiers.values())
            .find(amp => amp.getName() === 'Modem');
    }

    getRegularAmplifiers() {
        return Array.from(this.amplifiers.values())
            .filter(amp => amp.getName() !== 'Central' && amp.getName() !== 'Modem');
    }

    getAllAmplifiers() {
        return Array.from(this.amplifiers.values());
    }

    getAmplifiersWithPendingChanges() {
        return Array.from(this.amplifiers.values())
            .filter(amp => amp.hasPendingChanges());
    }

    getBridgeMode() {
        return this.bridgeMode;
    }

    getAllowNewAmplifiers() {
        return this.allowNewAmplifiers;
    }

    getSyncTimer() {
        return this.syncTimer;
    }

    getOfflineTimer() {
        return this.offlineTimer;
    }

    // Métodos de Atualização
    async updateAmplifierName(data) {
        try {
            const amplifier = this.amplifiers.get(data.serialNumber);
            if (!amplifier) {
                throw new Error("Amplificador não encontrado");
            }

            amplifier.setName(data.name);
            const databaseObject = amplifier.toDatabaseForNameUpdate();
            databaseObject.DS_NUMSERIE = databaseObject.DS_NUMSERIE.toUpperCase();

            const response = await fetch(`${process.env.REACT_APP_URL}/amplifier/name`, {
                method: 'POST',
                headers: {
                    "content-type": "application/json",
                    "Authorization": "Bearer " + localStorage.getItem("token")
                },
                body: JSON.stringify(databaseObject)
            });

            if (!response.ok) {
                const error = await response.json();
                throw Error(error.message || "Não foi possível atualizar o nome do amplificador");
            }
            
            // Atualiza o amplificador no Map
            // const amplifier = this.amplifiers.get(data.serialNumber);
            // if (amplifier) {
            //     amplifier.setName(data.name);
            // }
            
            this.notifySubscribers();
            return true;
        } catch (error) {
            // Em caso de erro, reverte a mudança
            const amplifier = this.amplifiers.get(data.serialNumber);
            if (amplifier) {
                amplifier.clearPendingChanges();
            }
            throw error;
        }
    }

    async updateAmplifierGroup(data) {
        try {
            const amplifier = this.amplifiers.get(data.serialNumber);
            if (!amplifier) {
                throw new Error("Amplificador não encontrado");
            }

            // Prepara o objeto do amplificador para o backend
            const updatedAmplifier = amplifier.toDatabase();

            const response = await fetch(`${process.env.REACT_APP_URL}/amplifier/update/group`, {
                method: 'POST',
                headers: {
                    "content-type": "application/json",
                    "Authorization": "Bearer " + localStorage.getItem("token")
                },
                body: JSON.stringify({
                    group: data.group, // Objeto grupo completo
                    amplifier: updatedAmplifier // Amplificador convertido para formato do banco
                })
            });

            if (!response.ok) throw new Error("Falha ao atualizar grupo");
            
            // Atualiza o amplificador no Map
            amplifier.setGroup(data.group.vL_CADASTRO, data.group.dS_LOCALIZACAO);

            // Reordena os amplificadores por grupo
            const sortedAmplifiers = Array.from(this.amplifiers.entries()).sort((a, b) => {
                const groupA = a[1].getGroupId()?.slice(12) || '';
                const groupB = b[1].getGroupId()?.slice(12) || '';
                return groupA.localeCompare(groupB);
            });

            // Reconstrói o Map com a nova ordenação
            this.amplifiers.clear();
            sortedAmplifiers.forEach(([key, value]) => {
                this.amplifiers.set(key, value);
            });
            
            this.notifySubscribers();
            return true;
        } catch (error) {
            console.error("Erro ao atualizar grupo:", error);
            throw error;
    }
    }

    // Métodos públicos para uso direto
    async updateAmplifier(serialNumber, updates) {
        try {
            // Coloca na fila as atualizações necessárias
            if (updates.name) {
                this.queueChange(serialNumber, 'UPDATE_NAME', {
                    serialNumber,
                    name: updates.name
                });
            }

            if (updates.group) {
                this.queueChange(serialNumber, 'UPDATE_GROUP', {
                    group: updates.group,
                    amplifier: {
                        serialNumber,
                        groupId: updates.group.vL_CADASTRO,
                        location: updates.group.dS_LOCALIZACAO
                    }
                });
            }

            // Processa a fila
            await this.processQueue();
            return true;
        } catch (error) {
            console.error('Erro ao atualizar amplificador:', error);
            throw error;
        }
    }

    async deleteAmplifier(serialNumber) {
        try {
            const response = await fetch(`${process.env.REACT_APP_URL}/amplifier/delete?ds_dl=${serialNumber}`, {
                method: "DELETE",
                headers: {
                    "content-type": "application/json",
                    "Authorization": "Bearer " + localStorage.getItem("token")
                }
            });

            if (!response.ok) {
                throw new Error("Não foi possível excluir o dispositivo");
            }

            // Remove o amplificador do Map
            this.amplifiers.delete(serialNumber);
            
            // Notifica os subscribers da mudança
            this.notifySubscribers();
            
            return true;
        } catch (error) {
            console.error("Erro ao excluir dispositivo:", error);
            throw error;
        }
    }

    async setBridgeMode(value) {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_URL}/userfetchdata/change/status?selector=${value ? 6 : 3}`, 
                {
                    method: "GET",
                    headers: {
                        "content-type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("token")
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Falha ao alterar o modo do amplificador');
            }

            this.bridgeMode = value;
            sessionStorage.setItem("bridgeMode", value);
            this.notifySubscribers();

            return true;
        } catch (error) {
            console.error("Erro ao alterar modo bridge:", error);
            throw error;
        }
    }

    async loadBridgeMode() {
        try {
            const response = await fetch(`${process.env.REACT_APP_URL}/userfetchdata/broadcast/mode`, {
                method: 'GET',
                headers: {
                    "content-type": "application/json",
                    "Authorization": "Bearer " + localStorage.getItem("token")
                }
            });

            if (!response.ok) {
                throw new Error("Não foi possível obter o modo de operação");
            }

            const operationMode = await response.json();
            // Modo 6/9 é bridge, modo 3 é normal
            this.bridgeMode = operationMode === 6 || operationMode === 9;
            sessionStorage.setItem("bridgeMode", this.bridgeMode);
            this.notifySubscribers();

            return this.bridgeMode;
        } catch (error) {
            console.error("Erro ao carregar modo bridge:", error);
            throw error;
        }
    }

    // New Amplifiers Control
    async setAllowNewAmplifiers(value) {
        try {
            const serialNumber = sessionStorage.getItem("serial");
            const response = await fetch(
                `${process.env.REACT_APP_URL}/generalconfigs/shownewamplifiers/set/${serialNumber}/${value}`,
                {
                    method: 'POST',
                    headers: {
                        "content-type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("token")
                    }
                }
            );

            if (!response.ok) {
                throw new Error("Não foi possível alterar a configuração de exibição de novos amplificadores");
            }

            const result = await response.json();
            this.allowNewAmplifiers = result;
            this.notifySubscribers();

            return result;
        } catch (error) {
            console.error("Erro ao alterar permissão de novos amplificadores:", error);
            throw error;
        }
    }

    async loadAllowNewAmplifiers(panelSerial) {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_URL}/generalconfigs/shownewamplifiers/get/${panelSerial}`,
                {
                    method: 'GET',
                    headers: {
                        "content-type": "application/json"
                    }
                }
            );

            if (!response.ok) {
                throw new Error("Não foi possível obter as configurações de exibição de novos amplificadores");
            }

            const result = await response.json();
            this.allowNewAmplifiers = result;
            this.notifySubscribers();

            return result;
        } catch (error) {
            console.error("Erro ao carregar configuração de novos amplificadores:", error);
            throw error;
        }
    }

    async loadTimers() {
        try {
            const response = await fetch(`${process.env.REACT_APP_URL}/amplifier/timer/converted`, {
                method: 'GET',
                headers: {
                    "content-type": "application/json",
                    "Authorization": "Bearer " + localStorage.getItem("token")
                }
            });

            if (!response.ok) {
                throw new Error("Não foi possível obter as informações de tempo!");
            }

            const result = await response.json();
            this.syncTimer = result.searching;
            this.offlineTimer = result.offline;
            this.notifySubscribers();

            return result;
        } catch (error) {
            console.error("Erro ao carregar timers:", error);
            throw error;
        }
    }

    async setTimers(syncTimer, offlineTimer) {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_URL}/amplifier/timer?searching=${syncTimer}&offline=${offlineTimer}`, 
                {
                    method: "GET",
                    headers: {
                        "content-type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("token")
                    }
                }
            );

            if (!response.ok) {
                throw new Error("Não foi possível finalizar a requisição de alteração do Tempo de Sincronização");
            }

            const result = await response.json();
            if (!result.status) {
                throw new Error(result.message);
            }

            this.syncTimer = syncTimer;
            this.offlineTimer = offlineTimer;
            this.notifySubscribers();

            return true;
        } catch (error) {
            console.error("Erro ao alterar timers:", error);
            throw error;
        }
    }

    queueChange(serialNumber, type, data) {
        if (!this.changeQueue) {
            this.changeQueue = new Map();
        }

        if (!this.changeQueue.has(serialNumber)) {
            this.changeQueue.set(serialNumber, []);
        }

        this.changeQueue.get(serialNumber).push({ type, data });
        this.notifySubscribers();
        return true;
    }

    async queueDelete(serialNumber) {
        try {
            this.queueChange(serialNumber, 'DELETE', null);
            await this.processQueue();
            return true;
        } catch (error) {
            console.error('Erro ao excluir amplificador:', error);
            throw error;
        }
    }

    // Processamento da Fila
    async processQueue() {
        if (this.processingQueue || this.changeQueue.size === 0) return;

        try {
            this.processingQueue = true;
            this.notifySubscribers();

            for (const [serialNumber, changes] of this.changeQueue.entries()) {
                for (const change of changes) {
                    switch (change.type) {
                        case 'UPDATE_NAME':
                            await this.updateAmplifierName(change.data);
                            break;
                        case 'UPDATE_GROUP':
                            await this.updateAmplifierGroup({
                                serialNumber,
                                group: change.data.group 
                            });
                            break;
                        case 'DELETE':
                            await this.deleteAmplifier(serialNumber);
                            break;
                        default:
                            console.warn('Tipo de alteração desconhecido:', change.type);
                    }
                }
            }

            this.changeQueue.clear();
        } catch (error) {
            console.error('Erro ao processar fila:', error);
            throw error;
        } finally {
            this.processingQueue = false;
            this.notifySubscribers();
        }
    }

    // Adicionar após os métodos de atualização
    handleNewAmplifier(message) {
        try {
            // Cria novo amplificador a partir dos dados recebidos
            const newAmplifier = Amplifier.fromDatabase(message);
            
            // Adiciona/Atualiza no map
            this.amplifiers.set(newAmplifier.getSerialNumber(), newAmplifier);
            
            // Notifica alteração
            this.notifySubscribers();
            
            return newAmplifier;
        } catch (error) {
            console.error("Erro ao processar novo amplificador:", error);
            throw error;
        }
    }

    handleAmplifierUpdate(message) {
        try {
            // message format: { amplifier: string, status: string }
            const amplifier = this.getAmplifier(message.amplifier);
            
            if (amplifier) {
                // Atualiza status
                amplifier.setStatus(message.status);
                
                // Se for remoto, atualiza também no backend
                if (process.env.REACT_APP_MODE === 'desktop') {
                    fetch(`${process.env.REACT_APP_API_URL}/amplifier/remote`, {
                        method: 'POST',
                        headers: {
                            "content-type": "application/json",
                            "Authorization": "Bearer " + localStorage.getItem("token")
                        },
                        body: JSON.stringify({
                            "amplifierSerial": message.amplifier,
                            "status": message.status
                        })
                    }).catch(error => console.error("Erro ao atualizar status remoto:", error));
                }

                this.notifySubscribers();
            }
        } catch (error) {
            console.error("Erro ao processar atualização de amplificador:", error);
            throw error;
        }
    }

    // Manipulação de Alarmes
    handleAlarmState() {
        const hasPendingChanges = this.getAmplifiersWithPendingChanges().length > 0;
        
        if (hasPendingChanges) {
            // Limpa todas as alterações pendentes
            this.amplifiers.forEach(amp => amp.clearPendingChanges());
            this.notifySubscribers();
        }
        
        return hasPendingChanges;
    }

    subscribe(callback) {
        this.subscribers.push(callback);
        // Retornamos uma função de cleanup
        return () => {
            this.subscribers = this.subscribers.filter(cb => cb !== callback);
        };
    }

    // Notifica todos os inscritos
    notifySubscribers() {
        const currentState = this.getState();
        if (!this.lastState || !isEqual(this.lastState, currentState)) {
            this.lastState = currentState;
            this.subscribers.forEach(callback => callback(currentState));
        }
    }

    // Retorna o estado atual
    getState() {
        return {
            amplifiers: this.getRegularAmplifiers(),
            central: this.getCentral(),
            modem: this.getModem(),
            hasPendingChanges: this.getAmplifiersWithPendingChanges().length > 0,
            isProcessing: this.processingQueue,
            initialized: this.initialized,
            currentSerial: sessionStorage.getItem("serial"),
            bridgeMode: this.bridgeMode,
            allowNewAmplifiers: this.allowNewAmplifiers,
            syncTimer: this.syncTimer,
            offlineTimer: this.offlineTimer
        };
    }
}

const amplifiersProvider = new AmplifiersProvider();

// Registra no window para debug
if (process.env.NODE_ENV === 'development') {
    window.amplifiersProvider = amplifiersProvider;
}

export default amplifiersProvider;